import * as React from 'react'
import { useState, useEffect } from 'react';
import EquationComponent from './EquationComponent';
import KeyPadComponent from "./KeyPadComponent";
import PartTextComponent from "./PartTextComponent";
import DeleteConfirmationModalComponent from './DeleteConfirmationModalComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { CPPart } from '../models/CPPart';
import { CPVariable } from '../models/CPVariable';
import { OptionValue } from '../../Global/models/OptionValue';
import LabelTextComponent from './LabelTextComponent';
import { Collapse } from 'react-bootstrap';
const cloneDeep = require("lodash.clonedeep");

export interface PartBuilderProps {
    selectedPart: CPPart,
    //setSelectedPart: Function,
    selectedPartIndex: number,
    setSelectedPartIndex: Function,
    savePart: Function,
    variables: Array<CPVariable>,
    userIsOwner: boolean,
    limitedEditing: boolean,
    assignmentLimitedEditing?: boolean,
}

function PartBuilder(props: PartBuilderProps) {
    const [equation, setEquation] = useState(props.selectedPart.equation)
    const [partText, setPartText] = useState(props.selectedPart.text || "")
    const [equationCursorPosition, setEquationCursorPosition] = useState(0)
    const [partAnswerLabel, setPartAnswerLabel] = useState(props.selectedPart.answerLabel)
    const [partUnitsLabel, setPartUnitsLabel] = useState(props.selectedPart.unitsLabel)
    const [multipleChoice, setMultipleChoice] = useState(props.selectedPart.multipleChoice)
    const [scientificNotation, setScientificNotation] = useState(props.selectedPart.scientificNotation)
    const [calculateAsDouble, setCalculateAsDouble] = useState(props.selectedPart.calculateAsDouble)
    const [options, setOptions] = useState(props.selectedPart.options)
    const [angleMode, setAngleMode] = useState("Degrees")
    const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | undefined>()
    const [selectedPartIndex, setSelectedPartIndex] = useState(props.selectedPartIndex)
    const [showComputationalPrecisionInfo, setShowComputationalPrecisionInfo] = useState(false)

    useEffect(() => {
        var editPart: CPPart = cloneDeep(props.selectedPart)
        editPart.equation = equation
        editPart.text = partText
        editPart.answerLabel = partAnswerLabel
        editPart.unitsLabel = partUnitsLabel
        editPart.multipleChoice = multipleChoice
        editPart.scientificNotation = scientificNotation
        editPart.calculateAsDouble = calculateAsDouble,
            editPart.options = options
        props.savePart(editPart)
    }, [equation, partText, partAnswerLabel, partUnitsLabel, multipleChoice, scientificNotation, calculateAsDouble, options])

    useEffect(() => {
        setEquation(props.selectedPart.equation)
        setPartText(props.selectedPart.text || "")
        setEquationCursorPosition(0)
        setPartAnswerLabel(props.selectedPart.answerLabel)
        setPartUnitsLabel(props.selectedPart.unitsLabel)
        setMultipleChoice(props.selectedPart.multipleChoice)
        setScientificNotation(props.selectedPart.scientificNotation)
        setCalculateAsDouble(props.selectedPart.calculateAsDouble)
        setOptions(props.selectedPart.options)
        setSelectedOptionIndex(undefined)
        setSelectedPartIndex(props.selectedPartIndex)
    }, [props.selectedPartIndex])

    const changeEquation = input => {
        setEquation(input.value)
        setEquationCursorPosition(input.selectionStart)
    }

    const equationKeypadClick = button => {

        let buttonValue = button.dataset.name
        let buttonCaret = parseInt(button.dataset.cursorposition)
        if ((equation + buttonValue).length < (equationCursorPosition + buttonCaret)) {
            buttonCaret = 0;
        }
        if (equationCursorPosition + buttonCaret < 0) {
            buttonCaret = 0;
        }

        if (buttonValue === "C") {
            reset()
        }
        else if (buttonValue === "CE") {
            backspace()
        }

        else {
            var e = document.getElementById("equationInput") as HTMLInputElement
            var start = e.selectionStart
            var end = e.selectionEnd
            var overwrite = button.dataset.overwrite
            var newEquation
            if (start == end || overwrite == "true") {
                newEquation = equation.slice(0, start!) + buttonValue + equation.slice(end!)
                e.value = newEquation
                if (start == null) {
                    start = 0
                }
                e.selectionStart = e.selectionEnd = start + buttonCaret
                setEquation(newEquation)
                setEquationCursorPosition(e.selectionStart)
            }
            else if (overwrite == "wrap") {
                newEquation = equation.slice(0, start!) + "(" + equation.slice(start!, end!) + ")" + equation.slice(end!)
                e.value = newEquation
                if (end == null) {
                    end = 0
                }
                e.selectionStart = e.selectionEnd = end + 2
                setEquation(newEquation)
                setEquationCursorPosition(e.selectionStart)
            }
            else {
                newEquation = equation.slice(0, start!) + buttonValue.slice(0, buttonCaret) + equation.slice(start!, end!) + buttonValue.slice(buttonCaret, buttonValue.length) + equation.slice(end!)
                if (start == null) {
                    start = 0
                }
                if (end == null) {
                    end = 0
                }
                var selectionDifference = end - start
                e.value = newEquation
                e.selectionStart = e.selectionEnd = start + parseInt(button.dataset.selectcursorposition) + selectionDifference
                setEquation(newEquation)
                setEquationCursorPosition(e.selectionStart)
            }
            e.focus()
        }
    };

    const reset = () => {
        setEquation("")
        setEquationCursorPosition(0)
        let e = document.getElementById("equationInput") as HTMLInputElement
        e.value = ""
        e.selectionStart = e.selectionEnd = 0
        e.focus()
    };

    const backspace = () => {
        if (equationCursorPosition > 0) {
            let newEquation = equation.slice(0, equationCursorPosition - 1)
            newEquation += equation.slice(equationCursorPosition)
            setEquation(newEquation)
            setEquationCursorPosition(equationCursorPosition - 1)
            let e = document.getElementById("equationInput") as HTMLInputElement
            e.value = newEquation
            e.selectionStart = e.selectionEnd = equationCursorPosition - 1
            e.focus()
        }
    };

    const changeAngleMode = () => {
        if (angleMode === "Degrees") {
            var updatedEquation = equation
            updatedEquation = updatedEquation.replace(/SinDeg\(/g, "Sin(")
            updatedEquation = updatedEquation.replace(/CosDeg\(/g, "Cos(")
            updatedEquation = updatedEquation.replace(/TanDeg\(/, "Tan(")
            var updatedCursorPosition = updatedEquation.length
            setAngleMode("Radians")
            setEquation(updatedEquation)
            setEquationCursorPosition(updatedCursorPosition)
            var e = document.getElementById('equationInput') as HTMLInputElement
            e.value = updatedEquation
            e.selectionStart = e.selectionEnd = updatedCursorPosition
            e.focus()
        }
        else {
            var updatedEquation = equation
            updatedEquation = updatedEquation.replace(/Sin\(/g, "SinDeg(")
            updatedEquation = updatedEquation.replace(/Cos\(/g, "CosDeg(")
            updatedEquation = updatedEquation.replace(/Tan\(/g, "TanDeg(")
            var updatedCursorPosition = updatedEquation.length
            setAngleMode("Degrees")
            setEquation(updatedEquation)
            setEquationCursorPosition(updatedCursorPosition)
            let e = document.getElementById('equationInput') as HTMLInputElement
            e.value = updatedEquation
            e.selectionStart = e.selectionEnd = updatedCursorPosition
            e.focus()
        }
    }

    const addOption = () => {
        var updatedOptions: Array<OptionValue> = []
        if (options) {
            updatedOptions = cloneDeep(options)
        }
        updatedOptions = updatedOptions.concat({ text: "", value: "" })
        setOptions(updatedOptions)
    }

    const changeOptionText = (text, index) => {
        var updatedOptions: Array<OptionValue> = []
        if (options) {
            updatedOptions = cloneDeep(options)
        }
        updatedOptions[index].text = text
        setOptions(updatedOptions)
    }

    const changeOptionValue = (value, index) => {
        var updatedOptions: Array<OptionValue> = []
        if (options) {
            updatedOptions = cloneDeep(options)
        }
        updatedOptions[index].value = value
        setOptions(updatedOptions)
    }

    const deleteOption = () => {
        var updatedOptions: Array<OptionValue> = []
        if (options) {
            updatedOptions = cloneDeep(options)
        }
        updatedOptions.splice(selectedOptionIndex as number, 1)
        setOptions(updatedOptions)
        setSelectedOptionIndex(undefined)
    }

    const changePartAnswerLabel = (label: string) => {
        if (label.length <= 200) {
            setPartAnswerLabel(label)
        }
    }

    const changePartUnitsLabel = (label: string) => {
        if (label.length <= 200) {
            setPartUnitsLabel(label)
        }
    }

    return (
        <div className="row">
            <div className="col">
                <PartTextComponent
                    variables={props.variables}
                    partText={partText}
                    changePartText={setPartText}
                    userIsOwner={props.userIsOwner}
                    limitedEditing={props.limitedEditing}
                    selectedPartIndex={selectedPartIndex}
                />
                <EquationComponent
                    userIsOwner={props.userIsOwner}
                    limitedEditing={props.limitedEditing}
                    equation={equation}
                    equationCursorPosition={equationCursorPosition}
                    changeEquation={changeEquation}
                />
                <KeyPadComponent
                    equationKeypadClick={equationKeypadClick}
                    changeAngleMode={changeAngleMode}
                    variables={props.variables}
                    angleMode={angleMode}
                    userIsOwner={props.userIsOwner}
                    limitedEditing={props.limitedEditing}
                />
                <div className="row my-2">
                    <div className="col-md-6">
                        <b>Answer Label:</b>
                        <LabelTextComponent labelText={partAnswerLabel} selectedPartIndex={selectedPartIndex} changeLabelText={changePartAnswerLabel} userIsOwner={props.userIsOwner} limitedEditing={props.limitedEditing} />
                    </div>
                    <div className="col-md-6">
                        <b>Units Label:</b>
                        <LabelTextComponent labelText={partUnitsLabel} selectedPartIndex={selectedPartIndex} changeLabelText={changePartUnitsLabel} userIsOwner={props.userIsOwner} limitedEditing={props.limitedEditing} />
                    </div>
                    {/* <div className="col-md-4">
                            <b>Answer Accepted Deviation:</b>
                            <div className="row">
                                <div className="col">
                                    <input type="number" min={0} step={1} className="form-control" onChange={e => setPlusMinus(parseInt(e.currentTarget.value))} value={plusMinus} />
                                </div>
                                <div className="col-auto">
                                    <select className="form-select" value={plusMinusIsPercent.toString()} onChange={e => setPlusMinusIsPercent(e.currentTarget.value == "true")}>
                                        <option value={"true"}>%</option>
                                        <option value={"false"}>{partUnitsLabel ? partUnitsLabel : "Amount"}</option>
                                    </select>
                                </div>
                            </div>
                        </div>*/}
                </div>
                <div className="row my-2">
                    <div className="row">
                        <div className="col">
                            <b>Multiple Choice:&emsp;</b>
                            <div className="form-check form-check-inline">
                                <input role="button" disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="multipleChoiceRadio" id="multipleChoiceTrue" checked={multipleChoice} onChange={() => setMultipleChoice(true)} />
                                <label className="form-check-label" htmlFor="multipleChoiceTrue">Yes</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input role="button" disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="multipleChoiceRadio" id="multipleChoiceFalse" checked={!multipleChoice} onChange={() => setMultipleChoice(false)} />
                                <label className="form-check-label" htmlFor="multipleChoiceFalse">No</label>
                            </div>
                        </div>
                    </div>
                </div>

                {multipleChoice ?
                    <div className="row my-2">
                        <div className="col">
                            <b><label>Options&emsp;{!props.userIsOwner || props.limitedEditing ? "" :
                                <>
                                    <a role="button" onClick={() => addOption()}><FontAwesomeIcon icon={faPlus} /> Add</a>&emsp;{selectedOptionIndex == undefined || <a role="button" data-bs-toggle="modal" data-bs-target="#deleteOptionModal"><FontAwesomeIcon icon={faMinus} /> Remove</a>}
                                </>
                            }
                            </label></b>
                            <table className="table table-hover table-bordered table-light">
                                <thead>
                                    <tr>
                                        <th scope="col">Text</th>
                                        <th scope="col">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {options ? options.map((option, optionIndex) => (
                                        <tr onClick={() => setSelectedOptionIndex(optionIndex)} key={optionIndex} className={selectedOptionIndex == optionIndex ? "table-active" : ""}>
                                            <td><input disabled={!props.userIsOwner} className="form-control" type="text" onFocus={() => setSelectedOptionIndex(optionIndex)} onChange={e => changeOptionText(e.currentTarget.value, optionIndex)} value={option.text}></input></td>
                                            <td><input disabled={!props.userIsOwner || props.limitedEditing} className="form-control" type="text" onFocus={() => setSelectedOptionIndex(optionIndex)} onChange={e => changeOptionValue(e.currentTarget.value, optionIndex)} value={option.value}></input></td>
                                        </tr>
                                    )
                                    ) : ""}
                                </tbody>
                            </table>
                        </div>
                        <DeleteConfirmationModalComponent
                            itemId="deleteOptionModal"
                            itemContent={"Do you want to delete the option " + (selectedOptionIndex == undefined || options![selectedOptionIndex].text) + "?"}
                            deleteFunction={deleteOption}
                        />
                    </div>

                    : ""}
                <div className="row my-2">
                    <div className="row">
                        <div className="col">
                            <b>Scientific Notation:&emsp;</b>
                            <div className="form-check form-check-inline">
                                <input role="button" maxLength={25} disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="scientificNotationRadio" id="scientificNotationTrue" checked={scientificNotation} onChange={() => setScientificNotation(true)} />
                                <label className="form-check-label" htmlFor="scientificNotationTrue">Yes</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input role="button" maxLength={25} disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="scientificNotationRadio" id="scientificNotationFalse" checked={!scientificNotation} onChange={() => setScientificNotation(false)} />
                                <label className="form-check-label" htmlFor="scientificNotationFalse">No</label>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row my-2">
                    <div className="row">
                        <div className="col">
                            <b>Computational Precision:&emsp;</b>
                            <div className="form-check form-check-inline">
                                <input role="button" disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="computationalPrecisionRadio" id="calculateAsDoubleFalse" checked={!calculateAsDouble} onChange={() => setCalculateAsDouble(false)} />
                                <label className="form-check-label" htmlFor="calculateAsDoubleFalse">Recommended</label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input role="button" disabled={!props.userIsOwner || props.limitedEditing} className="form-check-input" type="radio" name="computationalPrecisionRadio" id="calculateAsDoubleTrue" checked={calculateAsDouble} onChange={() => setCalculateAsDouble(true)} />
                                <label className="form-check-label" htmlFor="calculateAsDoubleTrue">Override</label>
                            </div>
                            <a className="text-link" onClick={() => setShowComputationalPrecisionInfo(!showComputationalPrecisionInfo)}>
                                More Info
                            </a>
                        </div>
                    </div>
                </div>
                <Collapse in={showComputationalPrecisionInfo}>
                    <div className="row">
                        <div className="col">
                            <div className="row">
                                <b>Recommended</b>
                                <p>
                                    Typically results in more accurate calculations. Use by default unless calculations are returning unexpected answers.
                                </p>
                            </div>
                            <div className="row">
                                <b>Override</b>
                                <p>
                                    Can provide better results when calculations include numbers greater than 10<sup>27</sup> or more precise than 10<sup>-27</sup>. Only use if recommended is returning unexpected results.
                                </p>
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
        </div>
    );
}



export default PartBuilder;